import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {
  Typography,
  Checkbox,
  FormControlLabel,
  Button
} from '@customink/pigment-react';
import {TextField} from '@mui/material';
import {StoreDetails} from 'models/storeDetails';
import {StoreSettings} from 'models/storeSettings';
import {useHistory} from 'react-router';
import {useSettingIsAvailable} from '../../hooks';
import Label from '../Label';
import AlertConfirmation from './AlertConfirmation';
import SettingDialog from './Dialog';
import {useUpdateSettingsMutation} from '../../api/storesApi';

type GroupCommunicationProps = {
  storeSlug: string;
  storeDetails: StoreDetails;
  storeSettings: StoreSettings;
};

const GroupCommunication: React.FC<GroupCommunicationProps> = ({
  storeSlug,
  storeDetails: {capabilities: storeCapabilities},
  storeSettings
}: GroupCommunicationProps): ReactElement => {
  const defaultValues: StoreSettings = {
    enableOrderPlacedEmail: false
  };
  const {settingIsAvailable} = useSettingIsAvailable();

  const [
    triggerUseUpdateSettingsMutation,
    {isSuccess: isEditSettingsSuccess, error: serverError}
  ] = useUpdateSettingsMutation();

  const unblockRef = useRef(null);

  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  const history = useHistory();
  const formMethods = useForm<StoreSettings>({
    defaultValues
  });
  const {
    control,
    formState: {isDirty: isFormDirty, isSubmitted: isFormSubmitted},
    reset: resetForm
  } = formMethods;

  /* 
    Form handlers
  */

  const handleUpdateSettingsSuccess = () => {
    if (isEditSettingsSuccess) {
      setShowSuccessAlert(true);
    }
  };

  const handleUpdateSettingsFailure = () =>
    setShowGeneralError(Boolean(serverError));

  const handleModalConfirm = () => {
    if (unblockRef) {
      unblockRef.current();
    }
    setShowLeaveDialog(false);
    history.push(currentPath);
  };

  const handleSubmit = (data: StoreSettings) => {
    triggerUseUpdateSettingsMutation({
      details: data,
      slug: storeSlug
    });
  };

  const handleCloseAlert = () => setShowSuccessAlert(false);

  /* 
    useEffect hooks
  */

  useEffect(() => {
    if (storeSettings) {
      const newValues = {
        ...defaultValues,
        enableOrderPlacedEmail: storeSettings.enableOrderPlacedEmail
      };
      resetForm({...newValues});
    }
  }, [storeSettings]);

  useEffect(() => {
    unblockRef.current = history.block((location: typeof history) => {
      if (isFormDirty && !isFormSubmitted) {
        setCurrentPath(location.pathname);

        setShowLeaveDialog(true);
        return false;
      }
      return true;
    });
    return () => unblockRef.current && unblockRef.current();
  }, [formMethods.formState, isFormDirty]);

  useEffect(handleUpdateSettingsSuccess, [isEditSettingsSuccess]);
  useEffect(handleUpdateSettingsFailure, [serverError]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleSubmit)} className="form">
        {showGeneralError && (
          <Typography variant="subHeading" className="generalError">
            Something went wrong. Please try again later.
          </Typography>
        )}
        <div className="form-field">
          <div className="tab-subheading">
            <Typography variant="subHeading">Group Communication</Typography>
          </div>
          <div className="sub-title-descrip">
            <Typography variant="bodyShort2">
              Let your group members know about how they can use the form and
              how they&apos;ll recieve their orders.
            </Typography>
          </div>
          {storeCapabilities?.includes('configureOrganizerEmails') ? (
            <div className="form-field-emails">
              <FormControlLabel
                control={
                  <Controller
                    render={({field}) => (
                      <Checkbox
                        className="purchase-email-checkbox"
                        onChange={field.onChange}
                        value={field.value}
                        checked={field.value}
                      />
                    )}
                    name="enableOrderPlacedEmail"
                    control={control}
                  />
                }
                label="Email me when a purchase is made"
              />
            </div>
          ) : null}
          <div className="form-field-notes setting-section">
            {settingIsAvailable('groupNote') ? (
              <>
                <Label>Note to Group (Shows on Group Order)</Label>
                <Controller
                  render={({field}) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      inputRef={field.ref}
                      placeholder="We'll use this form to collect everyone's information for the reunion t-shirts!"
                      multiline
                      rows={4}
                      fullWidth
                    />
                  )}
                  name="groupNote"
                  control={control}
                />
              </>
            ) : null}
            {settingIsAvailable('deliveryNote') ? (
              <>
                <Label>
                  Add Delivery or Distribution Instructions (Included in Emails
                  to Group Members)
                </Label>
                <Controller
                  render={({field}) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      inputRef={field.ref}
                      placeholder="ex. I'll hand out shirts at our reunion dinner."
                      multiline
                      rows={4}
                      fullWidth
                    />
                  )}
                  name="deliveryNote"
                  control={control}
                />
              </>
            ) : null}
          </div>
          <div className="settingsActions">
            <Button
              className="cancel-button"
              aria-label="reset"
              variant="link"
              disabled={!isFormDirty}
              onClick={() => {
                setShowGeneralError(false);
                formMethods.reset();
              }}>
              Cancel
            </Button>
            <Button
              className="submit-button"
              type="submit"
              disabled={!isFormDirty}>
              Save
            </Button>
          </div>
        </div>
      </form>
      <SettingDialog
        open={showLeaveDialog}
        title="Are you sure you want to leave without saving?"
        alertContent="You have unsaved changes. If you continue without saving, your recent changes will be lost."
        closeText="Go Back"
        confirmText="Continue Without Saving"
        handleConfirm={handleModalConfirm}
        handleClose={() => setShowLeaveDialog(false)}
      />
      <AlertConfirmation
        showSuccessAlert={showSuccessAlert}
        onCloseAlert={handleCloseAlert}
        groupOrderAlert={{
          message: 'Your group communication settings have been saved.'
        }}
      />
    </FormProvider>
  );
};

export default GroupCommunication;
